/**
 * Framework-free driver for the appearance orb: owns the rAF loop, pauses
 * when offscreen or the tab is hidden, and honours prefers-reduced-motion by
 * painting one deterministic frame instead of animating. The React
 * `<AgentOrb>` and the vanilla embed both delegate here.
 */
import { advanceClock, orbFrame, } from "./appearance.js";
import { createOrbEngine } from "./renderer.js";
/** Map the store's display state onto the orb's three-state motion model.
 *  `thinking`/`initializing` read as listening so the orb stays alive
 *  mid-turn instead of dropping to idle. */
export function displayStateToConversationState(state) {
    if (state === "speaking")
        return "talking";
    if (state === "listening" || state === "thinking" || state === "initializing")
        return "listening";
    return "idle";
}
export function createOrb(root, opts) {
    const wrap = document.createElement("div");
    wrap.setAttribute("aria-hidden", "true");
    wrap.style.cssText =
        "position:relative;overflow:hidden;border-radius:9999px;will-change:transform;";
    if (opts.size != null) {
        wrap.style.width = `${opts.size}px`;
        wrap.style.height = `${opts.size}px`;
    }
    else {
        wrap.style.width = "100%";
        wrap.style.height = "100%";
    }
    const canvas = document.createElement("canvas");
    canvas.style.cssText = "display:block;width:100%;height:100%;";
    wrap.appendChild(canvas);
    root.appendChild(wrap);
    let config = opts.appearance;
    let state = opts.state ?? "idle";
    const engine = createOrbEngine(canvas, { texture: opts.texture ?? true });
    if (!engine) {
        // No 2d context (jsdom, blocked canvas): keep layout, skip painting.
        return {
            el: wrap,
            setAppearance(next) {
                config = next;
            },
            setState(next) {
                state = next;
            },
            destroy() {
                wrap.remove();
            },
        };
    }
    const media = typeof window !== "undefined" && typeof window.matchMedia === "function"
        ? window.matchMedia("(prefers-reduced-motion: reduce)")
        : null;
    let raf = 0;
    let clock = 0;
    let last = 0;
    let onscreen = true;
    let running = false;
    const paintStatic = () => {
        const { t, scale } = orbFrame({ clock: 0, seconds: 0, state, reduced: true });
        wrap.style.transform = `scale(${scale})`;
        engine.draw(t, config);
    };
    const tick = (now) => {
        const dt = last ? Math.min(0.1, (now - last) / 1000) : 0;
        last = now;
        clock = advanceClock(clock, dt, state);
        const { t, scale } = orbFrame({ clock, seconds: now / 1000, state, reduced: false });
        wrap.style.transform = `scale(${scale.toFixed(4)})`;
        engine.draw(t, config);
        raf = requestAnimationFrame(tick);
    };
    const stop = () => {
        if (!running)
            return;
        running = false;
        cancelAnimationFrame(raf);
        raf = 0;
        last = 0;
    };
    const start = () => {
        if (running)
            return;
        running = true;
        raf = requestAnimationFrame(tick);
    };
    const sync = () => {
        if (media?.matches) {
            stop();
            paintStatic();
            return;
        }
        if (onscreen && !document.hidden)
            start();
        else
            stop();
    };
    const observer = typeof IntersectionObserver === "function"
        ? new IntersectionObserver((entries) => {
            onscreen = entries.some((e) => e.isIntersecting);
            sync();
        }, { rootMargin: "64px" })
        : null;
    observer?.observe(canvas);
    document.addEventListener("visibilitychange", sync);
    media?.addEventListener("change", sync);
    const resize = () => {
        if (media?.matches)
            paintStatic();
    };
    window.addEventListener("resize", resize);
    sync();
    return {
        el: wrap,
        setAppearance(next) {
            config = next;
            // Reduced motion has no loop, so a config change repaints itself.
            if (media?.matches)
                paintStatic();
        },
        setState(next) {
            state = next;
            if (media?.matches)
                paintStatic();
        },
        destroy() {
            stop();
            observer?.disconnect();
            document.removeEventListener("visibilitychange", sync);
            media?.removeEventListener("change", sync);
            window.removeEventListener("resize", resize);
            wrap.remove();
        },
    };
}
